import React, { useState } from 'react';
import {
  Box, Flex, Text, Badge, Separator,
  DialogRoot, DialogBackdrop, DialogPositioner, DialogContent,
  DialogHeader, DialogBody, DialogCloseTrigger,
} from '@chakra-ui/react';
import { Crown, Check, Loader2, X } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import Button from '../ui/Button';
import { createCheckoutSession, PRICING_PLANS } from '../../utils/paymentService';

const PaymentModal = ({ isOpen, onClose, selectedPlan = null, showAllPlans = true }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(selectedPlan || 'pro');

  const auth = getAuth();
  const user = auth.currentUser;

  const plans = showAllPlans
    ? [PRICING_PLANS.BESTSELLER, PRICING_PLANS.PRO]
    : [PRICING_PLANS[selectedPlan?.toUpperCase()]].filter(Boolean);

  const handlePayment = async (planId) => {
    if (!user) {
      setError('Please sign in to continue with payment');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const customer = {
        email: user.email,
        name: user.displayName || user.email.split('@')[0],
        userId: user.uid,
        firebaseUid: user.uid,
      };
      const returnUrl = `${window.location.origin}/payment/success?plan_id=${encodeURIComponent(planId)}&billing_cycle=${encodeURIComponent(billingCycle)}`;
      const result = await createCheckoutSession(planId, billingCycle, customer, returnUrl);
      if (result.success) {
        window.location.href = result.checkoutUrl;
      } else {
        setError(result.error || 'Failed to create payment session');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg" motionPreset="slide-in-bottom">
      <DialogBackdrop bg="rgba(0,0,0,0.5)" backdropFilter="blur(4px)" />
      <DialogPositioner>
        <DialogContent
          borderRadius="2xl"
          boxShadow="0 20px 60px rgba(0,0,0,0.2)"
          bg="var(--surface)"
          maxW="540px"
          w="full"
        >
          <DialogHeader borderBottomWidth="1px" borderColor="var(--border)" pb={4}>
            <Flex align="center" gap={3}>
              <Flex
                w="40px" h="40px" borderRadius="lg" align="center" justify="center"
                bg="linear-gradient(135deg, #1B4FCC 0%, #1540A8 100%)"
              >
                <Crown size={20} color="white" />
              </Flex>
              <Box>
                <Text fontWeight="700" fontSize="lg" color="var(--text-main)">Upgrade Your Plan</Text>
                <Text fontSize="sm" color="var(--text-muted)">Unlock unlimited syncs & advanced features</Text>
              </Box>
            </Flex>
            <DialogCloseTrigger asChild>
              <Flex as="button" w="32px" h="32px" borderRadius="md" align="center" justify="center" onClick={onClose}
                bg="transparent" border="none" cursor="pointer" color="var(--text-muted)" _hover={{ bg: 'var(--surface-alt)', color: 'var(--text-secondary)' }} transition="all 150ms">
                <X size={18} />
              </Flex>
            </DialogCloseTrigger>
          </DialogHeader>

          <DialogBody py={6} px={6}>
            {error && (
              <Flex align="center" gap={2} p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200" mb={4}>
                <Text fontSize="sm" color="red.700">{error}</Text>
              </Flex>
            )}

            {/* Billing Toggle */}
            <Flex align="center" justify="center" gap={3} mb={6}>
              <Flex align="center" gap={1}>
                <Text
                  fontSize="sm" fontWeight={billingCycle === 'monthly' ? '600' : '400'}
                  color={billingCycle === 'monthly' ? 'brand.600' : 'var(--text-muted)'}
                  cursor="pointer" onClick={() => setBillingCycle('monthly')}
                >Monthly</Text>
                <Badge colorPalette="green" variant="subtle" fontSize="10px" px={2} borderRadius="full">Save 15%</Badge>
              </Flex>
              <Box
                as="button" onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                w="44px" h="24px" borderRadius="full" position="relative" transition="all 200ms"
                bg={billingCycle === 'yearly' ? 'brand.600' : 'var(--border)'}
                border="none" cursor="pointer"
              >
                <Box
                  position="absolute" top="2px"
                  left={billingCycle === 'yearly' ? '22px' : '2px'}
                  w="20px" h="20px" borderRadius="full" bg="white"
                  boxShadow="0 1px 3px rgba(0,0,0,0.2)" transition="left 200ms"
                />
              </Box>
              <Flex align="center" gap={1}>
                <Text
                  fontSize="sm" fontWeight={billingCycle === 'yearly' ? '600' : '400'}
                  color={billingCycle === 'yearly' ? 'brand.600' : 'var(--text-muted)'}
                  cursor="pointer" onClick={() => setBillingCycle('yearly')}
                >Yearly</Text>
                <Badge colorPalette="green" variant="subtle" fontSize="10px" px={2} borderRadius="full">Save 30%</Badge>
              </Flex>
            </Flex>

            {/* Plan Cards */}
            <Flex gap={4} direction={{ base: 'column', sm: 'row' }}>
              {plans.map((plan) => {
                const pricing = plan[billingCycle];
                const fullMonthlyPricing = plan.fullMonthly;
                const fullYearlyPricing = plan.fullYearly;
                const isSelected = selectedPlanId === plan.id;
                const isPro = plan.id === 'pro';
                const originalPricing = billingCycle === 'yearly' ? fullYearlyPricing : fullMonthlyPricing;
                const showDiscount = Boolean(originalPricing);

                return (
                  <Box
                    key={plan.id}
                    flex={1}
                    borderRadius="xl"
                    borderWidth={isSelected ? '2px' : '1px'}
                    borderColor={isSelected ? 'brand.600' : isPro ? 'brand.200' : 'var(--border)'}
                    bg={isPro ? 'linear-gradient(135deg, #EEF4FF 0%, var(--surface) 60%)' : 'var(--surface)'}
                    p={5}
                    cursor="pointer"
                    onClick={() => setSelectedPlanId(plan.id)}
                    position="relative"
                    transition="all 200ms"
                    boxShadow={isSelected ? '0 0 0 3px rgba(27,79,204,0.15)' : 'none'}
                    _hover={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                  >
                    {isPro && (
                      <Badge
                        position="absolute" top="-10px" left="50%" transform="translateX(-50%)"
                        colorPalette="brand" variant="solid" fontSize="10px" fontWeight="700"
                        px={3} borderRadius="full" whiteSpace="nowrap"
                      >
                        Most Popular
                      </Badge>
                    )}

                    <Text fontWeight="700" fontSize="md" color="var(--text-main)" mb={1}>{plan.name}</Text>
                    <Box minH="64px" mb={4}>
                      <Text
                        fontSize="sm"
                        color="var(--text-muted)"
                        textDecoration="line-through"
                        visibility={showDiscount ? 'visible' : 'hidden'}
                        mb={showDiscount ? 0.5 : 0}
                      >
                        {originalPricing?.price || pricing.price}
                      </Text>
                      <Flex align="baseline" gap={1}>
                        <Text fontWeight="800" fontSize="2xl" color="var(--text-main)">{pricing.price}</Text>
                        <Text fontSize="sm" color="var(--text-muted)">
                          {billingCycle === 'monthly' ? '/month' : '/year'}
                        </Text>
                      </Flex>
                    </Box>

                    <Box as="ul" listStyle="none" p={0} m={0} mb={5} display="flex" flexDir="column" gap={2}>
                      {plan.features.map((feature, i) => (
                        <Flex key={i} align="center" gap={2}>
                          <Check size={14} color="#059669" strokeWidth={2.5} />
                          <Text fontSize="sm" color="var(--text-secondary)">{feature}</Text>
                        </Flex>
                      ))}
                    </Box>

                    <Button
                      variant={isPro ? 'primary' : 'secondary'}
                      size="medium"
                      onClick={(e) => { e.stopPropagation(); handlePayment(plan.id); }}
                      disabled={isLoading}
                      style={{ width: '100%' }}
                    >
                      {isLoading
                        ? <Flex align="center" gap={2}><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />Processing...</Flex>
                        : `Choose ${plan.name}`
                      }
                    </Button>
                  </Box>
                );
              })}
            </Flex>

            {billingCycle === 'yearly' && (
              <Flex align="center" justify="center" gap={2} mt={4}>
                <Check size={14} color="#059669" strokeWidth={2.5} />
                <Text fontSize="xs" color="teal.700" fontWeight="500">Save 30% with yearly billing</Text>
              </Flex>
            )}

            <Separator my={4} />
            <Text fontSize="xs" color="var(--text-muted)" textAlign="center">
              Secure payment powered by Dodo Payments
            </Text>
          </DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};

export default PaymentModal;
